const STORAGE_KEY = 'awp:session';

function newSessionId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    // RFC4122 v4 fallback (rare: old browsers / some test envs)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function readStoredSession() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return null;
    }
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.id === 'string' && typeof parsed.startIso === 'string') {
            return parsed;
        }
    } catch (_) {
        /* treat as missing */
    }
    return null;
}

function writeStoredSession(record) {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return false;
    }
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
        return true;
    } catch (_) {
        return false;
    }
}

/**
 * Create a new browser tracing session (new UUID + start timestamp).
 * Call on every successful login so LLM usage is grouped per sign-in.
 *
 * @param {string|null|undefined} userId authenticated user id to bind the session
 * @returns {{ sessionId: string, sessionStart: string }}
 */
export function createNewSession(userId = null) {
    const id = newSessionId();
    const startIso = new Date().toISOString();
    const record = {
        id,
        startIso,
        userId: userId || null,
    };
    writeStoredSession(record);
    return {sessionId: id, sessionStart: startIso};
}

/**
 * Remove the stored tracing session (call on logout).
 */
export function clearAwpSession() {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        return;
    }
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch (_) {
        /* ignore */
    }
}

/**
 * Return the current tab tracing session, creating one if missing.
 * When {@code userId} is provided and does not match the stored session's user,
 * a fresh session is created (covers user switch without logout).
 *
 * @param {string|null|undefined} userId
 * @returns {{ sessionId: string, sessionStart: string }}
 */
export function getOrCreateSession(userId = null) {
    if (typeof window === 'undefined' || !window.sessionStorage) {
        const sessionStart = new Date().toISOString();
        return {sessionId: 'no-storage', sessionStart};
    }

    const stored = readStoredSession();
    const boundUserId = userId || null;

    if (stored) {
        const storedUserId = stored.userId || null;
        if (!boundUserId || !storedUserId || storedUserId === boundUserId) {
            return {sessionId: stored.id, sessionStart: stored.startIso};
        }
    }

    return createNewSession(boundUserId);
}
